/* ==========================================================================
   oh-engine.js — Optimal Human 2026 animation/interaction engine (A2)

   Ported VERBATIM from the 2026 static-site inline <script> engines:
     - index.html                (reveal, header shrink, parallax, marquee/drift, mcta, lenis)
     - collection-preview.html   (header shrink, lenis)
     - founders-story.html       (header shrink, lenis)
     - product-templates/superfood-greens-gummies.html
                                 (anim/count-up engine, gallery drift physics, mcta, lenis)

   One namespaced IIFE. Physics, thresholds, easings and breakpoints are the
   source's — do not "normalize" them. Editor-safe: OH.scan(root) (re)binds
   everything; double-binding is guarded with data-oh-bound-* attributes and
   the engine rescans on shopify:section:load / shopify:section:reorder.

   Config gates: window.OHFLAGS (set by snippets/oh-tokens.liquid). Any flag
   that is missing defaults to TRUE — fresh install behaves exactly like the
   source site. Flags consumed: smoothScroll, reveals, anims, parallax,
   drift, headerShrink, mcta.
   ========================================================================== */
(function () {
  'use strict';

  /* double-load guard (the theme editor can re-inject scripts) */
  if (window.OH && window.OH.__engineLoaded) return;
  var OH = window.OH = window.OH || {};
  OH.__engineLoaded = true;

  /* ------------------------------------------------------------------ *
   * 1) CONFIG GATES
   * ------------------------------------------------------------------ */
  function flag(name) {
    var f = window.OHFLAGS;
    if (!f || typeof f !== 'object') return true;   /* missing = all true */
    return f[name] !== false;
  }
  /* computed once at load, exactly like every source engine */
  var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* collect(scope, sel) — querySelectorAll + the scope element itself
     (a section root passed by shopify:section:load can BE the match) */
  function collect(scope, sel) {
    var list = [].slice.call(scope.querySelectorAll(sel));
    if (scope.nodeType === 1 && scope.matches && scope.matches(sel)) list.unshift(scope);
    return list;
  }

  /* ------------------------------------------------------------------ *
   * 2) LENIS SMOOTH SCROLL (desktop only — mobile/touch keep native)
   * ------------------------------------------------------------------ */
  function initLenis() {
    if (OH.lenis) return;                                                      /* init once */
    if (prefersReduced) return;                                                /* respect reduced-motion */
    if (!(window.matchMedia && window.matchMedia('(min-width:1025px)').matches)) return;
    if (!flag('smoothScroll')) return;
    if (typeof Lenis === 'undefined') return;                                  /* asset missing → graceful native scroll */
    var lenis = new Lenis({ lerp: 0.066, smoothWheel: true, wheelMultiplier: 1 }); /* lerp 0.066 = ~40% more glide than 0.11; lower=smoother, higher=snappier */
    var docEl = document.documentElement, wasScrolling = false;
    function raf(t) {
      lenis.raf(t);
      var sc = !!lenis.isScrolling;
      if (sc !== wasScrolling) { wasScrolling = sc; docEl.classList.toggle('lenis-scrolling', sc); }
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    /* glide for in-page anchor links, offset for the sticky header.
       Delegated (vs the source's bind-at-load loop) so anchors inside
       editor-injected sections work without a rebind. */
    document.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id.length < 2) return;
      var el = null;
      try { el = document.querySelector(id); } catch (_) { el = null; }
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -90 });
    });
    window.__lenis = lenis;
    OH.lenis = lenis;
  }

  /* Lenis hijacks wheel events at the document level, which swallows nested
     wheel scroll inside Dawn's fixed overlays (cart drawer, menu drawer,
     predictive search). data-lenis-prevent tells Lenis to let these scroll
     containers scroll natively. Dawn also re-renders the drawer innards on
     every cart mutation (the Section Rendering API replaces .cart-items),
     dropping the attribute — so we re-apply on drawer open / subtree change. */
  var LENIS_PREVENT_SELECTORS = [
    'cart-drawer .drawer__inner',
    'cart-drawer .cart-items',
    'menu-drawer .menu-drawer__inner-container',
    'predictive-search .predictive-search__results'
  ];
  function applyLenisPrevent() {
    for (var i = 0; i < LENIS_PREVENT_SELECTORS.length; i++) {
      var nodes = document.querySelectorAll(LENIS_PREVENT_SELECTORS[i]);
      for (var j = 0; j < nodes.length; j++) nodes[j].setAttribute('data-lenis-prevent', '');
    }
  }
  var preventRaf = 0;
  function queueLenisPrevent() {
    if (preventRaf) return;
    preventRaf = requestAnimationFrame(function () { preventRaf = 0; applyLenisPrevent(); });
  }
  function watchDrawers() {
    if (typeof MutationObserver === 'undefined') return;
    var hosts = document.querySelectorAll('cart-drawer, menu-drawer, predictive-search');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (host.__ohPreventWatched) continue;
      host.__ohPreventWatched = true;
      /* class flips on open (cart-drawer gets .active/.animate), childList
         churn on re-render — both re-assert the prevent attributes */
      new MutationObserver(queueLenisPrevent)
        .observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
    }
  }

  /* ------------------------------------------------------------------ *
   * 3) SCROLL REVEAL — [data-reveal] → .is-visible  (index.html verbatim)
   * ------------------------------------------------------------------ */
  var revealIO = null;
  if (!prefersReduced && ('IntersectionObserver' in window)) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); revealIO.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  }
  function bindReveals(scope) {
    collect(scope, '[data-reveal]').forEach(function (el) {
      if (el.hasAttribute('data-oh-bound-reveal')) return;
      el.setAttribute('data-oh-bound-reveal', '');
      /* reduced-motion / no-IO / flag off → reveal-all fallback (content is never hidden) */
      if (!revealIO || !flag('reveals')) { el.classList.add('is-visible'); return; }
      revealIO.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * 4) PDP ANIM + COUNT-UP ENGINE — [data-anim] → .anim-in, [data-countup]
   *    (superfood-greens-gummies.html verbatim)
   * ------------------------------------------------------------------ */
  function runCount(el) {
    if (el.__counted) return; el.__counted = true;
    var target = parseFloat(el.getAttribute('data-countup')) || 0;
    var suffix = el.getAttribute('data-countup-suffix') || '';
    var decimals = parseInt(el.getAttribute('data-countup-decimals'), 10) || 0;
    var fmt = function (n) { return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }); };  /* 55000 -> "55,000" ; 4.9 (1 decimal) -> "4.9" */
    if (prefersReduced || !flag('anims') || target <= 0) { el.textContent = fmt(target) + suffix; return; }
    /* the review rating count-ups keep their original pace; every other count-up is slowed ~60% */
    var dur = el.closest('.pproof__rating') ? 1400 : 2240, t0 = null;
    function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3);                /* easeOutCubic (no overshoot: value never exceeds target, so 4.9 never rounds to 5) */
      var cur = decimals ? (e * target) : Math.round(e * target);
      el.textContent = fmt(cur) + suffix;
      if (p < 1) requestAnimationFrame(step); else el.textContent = fmt(target) + suffix;
    }
    requestAnimationFrame(step);
    setTimeout(function () { el.textContent = fmt(target) + suffix; }, dur + 300);  /* rAF-stall / hidden-tab failsafe */
  }
  var animIO = null, countIO = null;
  if (!prefersReduced && ('IntersectionObserver' in window)) {
    animIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.add('anim-in'); animIO.unobserve(en.target); } });
    }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
    countIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) { runCount(en.target); countIO.unobserve(en.target); } });
    }, { threshold: 0.45 });
  }
  function bindAnims(scope) {
    var els = collect(scope, '[data-anim]').filter(function (e) { return !e.hasAttribute('data-oh-bound-anim'); });
    var counts = collect(scope, '[data-countup]').filter(function (e) { return !e.hasAttribute('data-oh-bound-countup'); });
    if (!els.length && !counts.length) return;
    els.forEach(function (e) { e.setAttribute('data-oh-bound-anim', ''); });
    counts.forEach(function (e) { e.setAttribute('data-oh-bound-countup', ''); });
    var revealAll = function () { els.forEach(function (e) { e.classList.add('anim-in'); }); counts.forEach(runCount); };
    if (prefersReduced || !flag('anims') || !animIO) { revealAll(); return; }
    els.forEach(function (e) { animIO.observe(e); });
    counts.forEach(function (e) { countIO.observe(e); });
    setTimeout(revealAll, 3000);   /* failsafe: never leave content hidden (runCount is idempotent) */
  }

  /* ------------------------------------------------------------------ *
   * 5) SITE-WIDE SCROLL PARALLAX + AMBIENT FLOAT — [data-parallax]
   *    (index.html verbatim: batched offsetTop reads, no rect feedback)
   * ------------------------------------------------------------------ */
  var PAR_MULT = 2.5;    /* overall strength (one knob to tune the whole page) */
  var pData = [], pTick = false, pBound = false;
  var absTop = function (el) { var y = 0; while (el) { y += el.offsetTop; el = el.offsetParent; } return y; };
  var pUpdate = function () {
    pData = pData.filter(function (o) { return o.el.isConnected; });   /* editor may have removed a section */
    if (window.innerWidth <= 900) {   /* mobile/tablet: ZERO displacement — layout stays exactly as designed, overlap impossible */
      for (var m = 0; m < pData.length; m++) { var e = pData[m]; e.el.style.transform = ''; e.el.style.removeProperty('--py'); e.el.style.removeProperty('--psc'); }
      pTick = false; return;
    }
    var vh = window.innerHeight, sy = window.pageYOffset || document.documentElement.scrollTop || 0;
    /* READ phase — grouped, transform-independent (offsetTop) */
    for (var i = 0; i < pData.length; i++) { pData[i].top = absTop(pData[i].el); pData[i].h = pData[i].el.offsetHeight; }
    /* WRITE phase */
    for (var j = 0; j < pData.length; j++) {
      var o = pData[j];
      var prog = (o.top + o.h / 2 - sy - vh / 2) / vh;   /* + below center, - above */
      var off = prog * o.peak * 2;
      if (off > o.peak) off = o.peak; else if (off < -o.peak) off = -o.peak;
      var s = 1;
      if (o.psc) { s = 1 + prog * o.psc * 1.7; if (s > 1 + o.psc) s = 1 + o.psc; else if (s < 1 - o.psc) s = 1 - o.psc; }
      if (o.isFloat) { o.el.style.setProperty('--py', off.toFixed(2) + 'px'); if (o.psc) o.el.style.setProperty('--psc', s.toFixed(3)); }
      else { o.el.style.transform = 'translate3d(0,' + off.toFixed(2) + 'px,0)' + (o.psc ? ' scale(' + s.toFixed(3) + ')' : ''); }
    }
    pTick = false;
  };
  var pOnScroll = function () { if (!pTick) { pTick = true; requestAnimationFrame(pUpdate); } };
  function bindParallax(scope) {
    if (prefersReduced || !flag('parallax')) return;
    collect(scope, '[data-parallax]').forEach(function (el) {
      if (el.hasAttribute('data-oh-bound-parallax')) return;
      el.setAttribute('data-oh-bound-parallax', '');
      pData.push({
        el: el,
        peak: (parseFloat(el.getAttribute('data-parallax')) || 0) * PAR_MULT,
        psc: parseFloat(el.getAttribute('data-pscale')) || 0,
        isFloat: el.classList.contains('float-soft'),
        top: 0, h: 0
      });
    });
    if (!pData.length) return;
    if (!pBound) {
      pBound = true;
      window.addEventListener('scroll', pOnScroll, { passive: true });
      window.addEventListener('resize', pOnScroll, { passive: true });
      window.addEventListener('load', pUpdate);   /* re-settle after images/fonts shift layout */
    }
    pUpdate();
  }

  /* ------------------------------------------------------------------ *
   * 6) HEADER SHRINK — scrollY > 16 toggles .is-scrolled on [data-header]
   * ------------------------------------------------------------------ */
  var headers = [], hTick = false, hBound = false;
  function headerUpdate() {
    hTick = false;
    var on = window.scrollY > 16;
    for (var i = 0; i < headers.length; i++) {
      if (!headers[i].isConnected) continue;
      if (on) headers[i].classList.add('is-scrolled');
      else headers[i].classList.remove('is-scrolled');
    }
  }
  function bindHeader() {
    if (!flag('headerShrink')) return;
    headers = [].slice.call(document.querySelectorAll('[data-header]'));
    if (!headers.length) return;
    if (!hBound) {
      hBound = true;
      window.addEventListener('scroll', function () { if (!hTick) { hTick = true; requestAnimationFrame(headerUpdate); } }, { passive: true });
    }
    headerUpdate();
  }

  /* ------------------------------------------------------------------ *
   * 7) DRIFT/DRAG MARQUEE FACTORY — OH.drift(viewport, track, opts)
   *    Unified physics of the source's mq (product lineup), uq (UGC wheel)
   *    and PDP gallery engines:
   *      - eased velocity: v += (vT - v) * min(1, 4.5*dt), dt clamped 0.032
   *      - pointer capture ONLY after a 6px drag (the tap-vs-drag capture
   *        fix from the PDP gallery: capturing at pointerdown retargets the
   *        click's e.target so a plain tap never resolves its card)
   *      - sample-ring fling (110ms window), clamped, decays into drift
   *      - seamless half-width wrap (track = two identical halves)
   *      - dragstart preventDefault (stops native link/media drag hijack)
   *      - document.hidden guard, capture-phase click suppression after drag
   *    opts: { base:26, flingMax:1800, dragThresh:6, resumeMs:1000,
   *            seedFn:fn(api), isPaused:fn()->bool }
   * ------------------------------------------------------------------ */
  OH.drift = function (viewport, track, opts) {
    if (!viewport || !track) return null;
    if (viewport.hasAttribute('data-oh-bound-drift')) return viewport.__ohDrift || null;
    viewport.setAttribute('data-oh-bound-drift', '');
    opts = opts || {};
    var BASE = (prefersReduced || !flag('drift')) ? 0 : (opts.base != null ? opts.base : 26);  /* px/sec ambient drift; 0 under reduced motion (drag still works) */
    var FLING_MAX = opts.flingMax != null ? opts.flingMax : 1800;
    var DRAG_THRESH = opts.dragThresh != null ? opts.dragThresh : 6;
    var RESUME_MS = opts.resumeMs != null ? opts.resumeMs : 1000;
    var seedFn = typeof opts.seedFn === 'function' ? opts.seedFn : null;

    var setW = 0, off = 0, v = 0, vT = BASE;
    var drag = false, moved = false, pid = null, lastX = 0, startX = 0, samp = [], lastTS = 0, resumeTimer = 0;

    function apply() { track.style.transform = 'translate3d(' + (-off).toFixed(2) + 'px,0,0)'; }
    function wrap() { if (setW > 0) { off %= setW; if (off < 0) off += setW; } }
    function measure() {
      var ratio = setW > 0 ? off / setW : 0;
      setW = track.scrollWidth / 2;    /* two identical halves → wrap at half-width is seamless */
      off = ratio * setW; wrap(); apply();
    }

    track.style.animation = 'none';    /* JS owns the motion — kill any CSS marquee fallback */

    function frame(ts) {
      var dt = Math.min(0.032, lastTS ? (ts - lastTS) / 1000 : 0.016); lastTS = ts;  /* dt clamp = tab-switch protection */
      var paused = typeof opts.isPaused === 'function' && opts.isPaused();
      if (!document.hidden && !paused) {
        v += (vT - v) * Math.min(1, 4.5 * dt);       /* eased velocity = smooth ease-in/out */
        if (!drag) { off += v * dt; wrap(); }
        apply();
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    function sample(t, x) { samp.push({ t: t, x: x }); var c = t - 110; while (samp.length > 1 && samp[0].t < c) samp.shift(); }

    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });  /* stop native link/media drag from hijacking the grab */
    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      drag = true; moved = false; pid = e.pointerId; startX = lastX = e.clientX; v = 0; vT = 0;   /* grip: kill motion instantly */
      if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = 0; }
      samp.length = 0; sample(performance.now(), e.clientX);
      viewport.classList.add('is-grabbing');
      /* NOTE: pointer capture is deliberately NOT taken here — see factory header */
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== pid) return;
      var dx = e.clientX - lastX; lastX = e.clientX;
      if (Math.abs(e.clientX - startX) > DRAG_THRESH) {
        if (!moved) { try { viewport.setPointerCapture(pid); } catch (_) { } }   /* capture only for a genuine drag */
        moved = true;
      }
      off -= dx; wrap(); apply();      /* content follows the finger/cursor */
      sample(performance.now(), e.clientX);
    });
    function end(e) {
      if (!drag || (e && e.pointerId !== pid)) return;
      drag = false; viewport.classList.remove('is-grabbing');
      try { viewport.releasePointerCapture(pid); } catch (_) { }
      var fv = 0;
      if (samp.length > 1) { var a = samp[0], b = samp[samp.length - 1], d = b.t - a.t; if (d > 0) fv = (b.x - a.x) / d * 1000; }
      if (fv > FLING_MAX) fv = FLING_MAX; else if (fv < -FLING_MAX) fv = -FLING_MAX;
      v = -fv;                         /* fling momentum, decays into drift */
      vT = BASE;
      pid = null;
    }
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
    viewport.addEventListener('pointerleave', end);
    /* a drag must never fire a click/navigation on the content (capture phase, before card handlers) */
    viewport.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);

    var rz;
    window.addEventListener('resize', function () {
      clearTimeout(rz);
      rz = setTimeout(function () { measure(); if (seedFn) seedFn(api); }, 200);
    }, { passive: true });

    var api = {
      viewport: viewport, track: track,
      measure: measure, wrap: wrap, apply: apply,
      getOffset: function () { return off; },
      setOffset: function (px) { off = px; wrap(); apply(); },
      /* glide to a stop, resume ambient after RESUME_MS (PDP thumb-click behavior) */
      pause: function (autoResume) {
        vT = 0;
        if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = 0; }
        if (autoResume !== false) resumeTimer = setTimeout(function () { resumeTimer = 0; vT = BASE; }, RESUME_MS);
      },
      resume: function () { vT = BASE; },
      setBase: function (b) { BASE = (prefersReduced || !flag('drift')) ? 0 : b; vT = BASE; }
    };
    measure();
    if (seedFn) seedFn(api);
    viewport.__ohDrift = api;
    return api;
  };

  /* auto-init: any [data-oh-drift] viewport, JSON opts in the attribute.
     Track = opts.track selector, else [data-oh-drift-track], else first child. */
  function bindDrift(scope) {
    collect(scope, '[data-oh-drift]').forEach(function (vp) {
      if (vp.hasAttribute('data-oh-bound-drift')) return;
      var o = {};
      try { o = JSON.parse(vp.getAttribute('data-oh-drift') || '{}') || {}; } catch (_) { o = {}; }
      var track = (o.track && vp.querySelector(o.track)) || vp.querySelector('[data-oh-drift-track]') || vp.firstElementChild;
      if (!track) return;
      OH.drift(vp, track, o);
    });
  }

  /* ------------------------------------------------------------------ *
   * 8) STICKY BAR (mcta) — [data-mcta] with data-mcta-trigger selector.
   *    Shows when the trigger's bottom < 0, hides near the page end
   *    (source: hero on the homepage, #buybox on the PDP; 700px end zone).
   * ------------------------------------------------------------------ */
  function bindMcta(scope) {
    collect(scope, '[data-mcta]').forEach(function (bar) {
      if (bar.hasAttribute('data-oh-bound-mcta')) return;
      bar.setAttribute('data-oh-bound-mcta', '');
      if (!flag('mcta')) return;
      var sel = bar.getAttribute('data-mcta-trigger');
      if (!sel) return;
      var trigger = null;
      try { trigger = document.querySelector(sel); } catch (_) { trigger = null; }
      if (!trigger) return;
      var ticking = false;
      var setFocusable = function (on) {
        /* aria-hidden bar must not keep focusable children in the tab order */
        var f = bar.querySelectorAll('a, button, input, select, textarea');
        for (var i = 0; i < f.length; i++) {
          if (on) f[i].removeAttribute('tabindex');
          else f[i].setAttribute('tabindex', '-1');
        }
      };
      var update = function () {
        ticking = false;
        if (!trigger.isConnected) {
          try { trigger = document.querySelector(sel); } catch (_) { trigger = null; }   /* editor re-rendered the trigger section */
          if (!trigger) return;
        }
        var past = trigger.getBoundingClientRect().bottom < 0;
        var nearEnd = (window.innerHeight + window.scrollY) > (document.body.scrollHeight - 700);
        var on = past && !nearEnd;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        setFocusable(on);
      };
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; requestAnimationFrame(update); setTimeout(update, 120); }   /* PDP source: late re-check after momentum settles */
      }, { passive: true });
      document.addEventListener('visibilitychange', update);
      update();
    });
  }

  /* ------------------------------------------------------------------ *
   * 10) EDITOR SAFETY — OH.scan(root) (re)binds everything for a root.
   * ------------------------------------------------------------------ */
  OH.scan = function (root) {
    var scope = (root && root.nodeType === 1) ? root : document;
    bindReveals(scope);
    bindAnims(scope);
    bindParallax(scope);
    bindDrift(scope);
    bindMcta(scope);
    bindHeader();
    applyLenisPrevent();   /* M1: exclude Dawn's nested scroll containers from Lenis */
    watchDrawers();
  };

  function boot() {
    initLenis();
    OH.scan(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) { OH.scan(e.target || document); });
  document.addEventListener('shopify:section:reorder', function () { OH.scan(document); });
})();
